import {
  ComposedChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  ReferenceLine,
  ResponsiveContainer,
} from "recharts";
import type { VerbalResultPanelProps, VerbalWordRecord } from "../../type/verbalTest";
import { useI18n } from "../../utils/translate/i18n/useI18n";
import FormActionButton from "../formActionButton/formActionButton";
import ResultHeader from "../resultHeader/resultHeader";
import "./verbalResultPanel.scss";

const LEFT_COLOR  = "#1a56db";
const RIGHT_COLOR = "#e02424";
const REF_COLOR   = "#111827";

// ── Reference (normative) speech audiogram curve ──────────────────────────────
// Represents expected % word recognition for a healthy adult ear (Polish monosyllabic word lists).
const REFERENCE_POINTS: ReadonlyArray<{ dB: number; ref: number }> = [
  { dB:  5, ref:   0 },
  { dB: 10, ref:   3 },
  { dB: 15, ref:  15 },
  { dB: 20, ref:  50 },
  { dB: 25, ref:  82 },
  { dB: 30, ref:  96 },
  { dB: 35, ref: 100 }
];

function getRefValue(dB: number): number {
  if (dB <= 0)   return 0;
  if (dB >= 35)  return 100;
  let lo: { dB: number; ref: number } | undefined;
  let hi: { dB: number; ref: number } | undefined;
  for (const p of REFERENCE_POINTS) {
    if (p.dB <= dB) lo = p;
    if (p.dB >= dB && !hi) { hi = p; break; }
  }
  if (!lo || !hi || lo.dB === hi.dB) return lo?.ref ?? hi?.ref ?? 100;
  const t = (dB - lo.dB) / (hi.dB - lo.dB);
  return Math.round(lo.ref + t * (hi.ref - lo.ref));
}

// ── Interpretation helpers ────────────────────────────────────────────────────
function srtInterpretation(srt: number | null, t: (s: string) => string): { label: string; className: string } {
  if (srt === null) return { label: t("Brak danych"), className: "text-muted" };
  if (srt <= 20)   return { label: t("Norma (≤20 dB)"),               className: "text-success" };
  if (srt <= 40)   return { label: t("Lekkie (21–40 dB)"),            className: "text-warning" };
  if (srt <= 55)   return { label: t("Umiarkowane (41–55 dB)"),        className: "text-warning" };
  if (srt <= 70)   return { label: t("Znaczne (56–70 dB)"),            className: "text-danger"  };
  return                  { label: t("Głębokie (>70 dB)"),             className: "text-danger"  };
}

function wrsInterpretation(wrs: number | null, t: (s: string) => string): { label: string; className: string } {
  if (wrs === null) return { label: t("Brak danych"), className: "text-muted" };
  if (wrs >= 90)   return { label: t("Doskonałe (90–100%)"),  className: "text-success" };
  if (wrs >= 80)   return { label: t("Dobre (80–89%)"),        className: "text-success" };
  if (wrs >= 70)   return { label: t("Dostateczne (70–79%)"), className: "text-warning" };
  return                  { label: t("Słabe (<70%)"),           className: "text-danger"  };
}

// ── Audiogram data builder ────────────────────────────────────────────────────
type AudiogramPoint = {
  dB: number;
  left?: number;
  right?: number;
  leftWrs: boolean;
  rightWrs: boolean;
  ref: number;  // normative reference value at this dB
};

function buildAudigramData(
  leftSrtWords:  VerbalWordRecord[],
  leftWrsWords:  VerbalWordRecord[],
  rightSrtWords: VerbalWordRecord[],
  rightWrsWords: VerbalWordRecord[],
): AudiogramPoint[] {
  type Entry = { correct: number; total: number; isWrs: boolean };
  const leftMap  = new Map<number, Entry>();
  const rightMap = new Map<number, Entry>();

  const add = (map: Map<number, Entry>, words: VerbalWordRecord[], isWrs: boolean) => {
    for (const w of words) {
      const e = map.get(w.dB) ?? { correct: 0, total: 0, isWrs: false };
      e.total++;
      if (w.heard) e.correct++;
      if (isWrs) e.isWrs = true;
      map.set(w.dB, e);
    }
  };

  add(leftMap,  leftSrtWords,  false);
  add(leftMap,  leftWrsWords,  true);
  add(rightMap, rightSrtWords, false);
  add(rightMap, rightWrsWords, true);

  const allDb = new Set([
    ...leftMap.keys(),
    ...rightMap.keys(),
    ...REFERENCE_POINTS.map(p => p.dB),
  ]);
  return Array.from(allDb)
    .sort((a, b) => a - b)
    .map(dB => {
      const l = leftMap.get(dB);
      const r = rightMap.get(dB);
      return {
        dB,
        left:     l ? Math.round((l.correct / l.total) * 100) : undefined,
        right:    r ? Math.round((r.correct / r.total) * 100) : undefined,
        leftWrs:  l?.isWrs  ?? false,
        rightWrs: r?.isWrs  ?? false,
        ref:      getRefValue(dB),
      };
    });
}

// Fallback for legacy results that lack word_results detail
function buildFallbackData(
  srtLeft:  number | null,
  srtRight: number | null,
  wrsLeft:  number | null,
  wrsRight: number | null,
): AudiogramPoint[] {
  const map = new Map<number, AudiogramPoint>();
  const touch = (dB: number): AudiogramPoint => {
    if (!map.has(dB)) map.set(dB, { dB, leftWrs: false, rightWrs: false, ref: getRefValue(dB) });
    return map.get(dB)!;
  };
  if (srtLeft  !== null) { const p = touch(srtLeft);  p.left    = 50; }
  if (srtRight !== null) { const p = touch(srtRight); p.right   = 50; }
  if (srtLeft  !== null && wrsLeft  !== null) {
    const db = Math.min(100, srtLeft  + 35);
    const p = touch(db); p.left = wrsLeft;  p.leftWrs  = true;
  }
  if (srtRight !== null && wrsRight !== null) {
    const db = Math.min(100, srtRight + 35);
    const p = touch(db); p.right = wrsRight; p.rightWrs = true;
  }
  // Ensure all reference dB levels are present
  for (const rp of REFERENCE_POINTS) touch(rp.dB);
  return Array.from(map.values())
    .sort((a, b) => a.dB - b.dB)
    .map(p => ({ ...p, ref: getRefValue(p.dB) }));
}

// ── Relative WRS: compares patient curve to normative reference ───────────────
// Weights every tested dB level by how many words were presented there, then
// computes what fraction of the "reference-expected correct" the patient achieved.
// Result: ~100% when curve matches reference; <100% when curve is shifted right.
function computeRelativeWRS(
  srtWords: VerbalWordRecord[],
  wrsWords: VerbalWordRecord[],
  absWrs:   number | null,
): number | null {
  if (absWrs === null) return null;
  const all = [...srtWords, ...wrsWords];
  if (all.length === 0) return absWrs;

  const byDb = new Map<number, { correct: number; total: number }>();
  for (const w of all) {
    const e = byDb.get(w.dB) ?? { correct: 0, total: 0 };
    e.total++;
    if (w.heard) e.correct++;
    byDb.set(w.dB, e);
  }

  let sumActual   = 0;
  let sumExpected = 0;
  for (const [dB, { correct, total }] of byDb) {
    sumActual   += correct;
    sumExpected += total * (getRefValue(dB) / 100);
  }
  if (sumExpected === 0) return absWrs;
  return Math.round(Math.min(100, (sumActual / sumExpected) * 100));
}

// ── Custom dot renderers (audiometric symbols) ────────────────────────────────
function LeftDot({ cx, cy, payload }: { cx?: number; cy?: number; payload?: AudiogramPoint }) {
  if (cx == null || cy == null || payload?.left == null) return null;
  if (payload.leftWrs) {
    return <rect x={cx - 6} y={cy - 6} width={12} height={12} fill={LEFT_COLOR} stroke={LEFT_COLOR} strokeWidth={1} />;
  }
  return (
    <text x={cx} y={cy} textAnchor="middle" dominantBaseline="central"
          fontSize={16} fontWeight="bold" fill={LEFT_COLOR} style={{ userSelect: "none" }}>×</text>
  );
}

function RightDot({ cx, cy, payload }: { cx?: number; cy?: number; payload?: AudiogramPoint }) {
  if (cx == null || cy == null || payload?.right == null) return null;
  if (payload.rightWrs) {
    return (
      <polygon
        points={`${cx},${cy - 7} ${cx - 6},${cy + 5} ${cx + 6},${cy + 5}`}
        fill={RIGHT_COLOR} stroke={RIGHT_COLOR} strokeWidth={1}
      />
    );
  }
  return <circle cx={cx} cy={cy} r={5} fill="none" stroke={RIGHT_COLOR} strokeWidth={2} />;
}

// ── Interpretation scale bar ──────────────────────────────────────────────────
type Zone = { from: number; to: number; color: string; label: string };

const SRT_ZONES: Zone[] = [
  { from: 0,   to: 25,  color: "#22c55e", label: "Norma"     },
  { from: 25,  to: 40,  color: "#84cc16", label: "Lekkie"    },
  { from: 40,  to: 55,  color: "#eab308", label: "Umiark."   },
  { from: 55,  to: 70,  color: "#f97316", label: "Znaczne"   },
  { from: 70,  to: 110, color: "#ef4444", label: "Głębokie"  },
];

const WRS_ZONES: Zone[] = [
  { from: 0,  to: 70,  color: "#ef4444", label: "Słabe"        },
  { from: 70, to: 80,  color: "#eab308", label: "Dostateczne"  },
  { from: 80, to: 90,  color: "#84cc16", label: "Dobre"        },
  { from: 90, to: 100, color: "#22c55e", label: "Doskonałe"    },
];

function ScaleBar({
  zones, max, valueLeft, valueRight, unit, title,
}: {
  zones: Zone[];
  max: number;
  valueLeft:  number | null;
  valueRight: number | null;
  unit: string;
  title: string;
}) {
  const { t } = useI18n();
  const pct = (v: number) => `${(v / max) * 100}%`;
  return (
    <div className="verbal-results__scale">
      <span className="verbal-results__scale-title">{title}</span>
      <div className="verbal-results__scale-track">
        {zones.map((z, i) => (
          <div
            key={i}
            className="verbal-results__scale-zone"
            style={{ left: pct(z.from), width: pct(z.to - z.from), background: z.color }}
          >
            <span className="verbal-results__scale-zone-label">{t(z.label)}</span>
          </div>
        ))}
        {valueLeft !== null && (
          <div
            className="verbal-results__scale-marker verbal-results__scale-marker--left"
            style={{ left: pct(Math.min(valueLeft, max)) }}
            title={`${t("Lewe")}: ${valueLeft}${unit}`}
          >
            <span className="verbal-results__scale-marker-tip" />
            <span className="verbal-results__scale-marker-label" style={{ color: LEFT_COLOR }}>
              {valueLeft}{unit}
            </span>
          </div>
        )}
        {valueRight !== null && (
          <div
            className="verbal-results__scale-marker verbal-results__scale-marker--right"
            style={{ left: pct(Math.min(valueRight, max)) }}
            title={`${t("Prawe")}: ${valueRight}${unit}`}
          >
            <span className="verbal-results__scale-marker-tip" />
            <span className="verbal-results__scale-marker-label" style={{ color: RIGHT_COLOR }}>
              {valueRight}{unit}
            </span>
          </div>
        )}
      </div>
      <div className="verbal-results__scale-axis">
        {zones.map((z, i) => (
          <span key={i} style={{ left: pct(z.from) }}>{z.from}{unit}</span>
        ))}
        <span style={{ left: "100%" }}>{max}{unit}</span>
      </div>
    </div>
  );
}

export default function VerbalResultPanel({
  result,
  resultPanelRef,
  onNewTest,
  subtitle,
  exportMenuRef,
  isExportMenuOpen,
  isExporting,
  exportFormat,
  isExportDisabled,
  showAnalysisButton,
  isAnalysisDisabled,
  onToggleExportMenu,
  onExport,
  onAnalyze,
  onEmailResult,
  children,
}: VerbalResultPanelProps) {
  const { t } = useI18n();

  // When used as a panel wrapper (subtitle provided) but result not yet loaded, still render the shell
  if (!result) {
    if (subtitle === undefined) return null;
    return (
      <div ref={resultPanelRef}>
        <div className="verbal-results">
          <ResultHeader pillText={t("Audiometria słowna")} subtitle={subtitle} />
          {children}
        </div>
      </div>
    );
  }

  const { srt, wrs, word_results } = result;

  // Build speech audiogram data
  const audiogramData: AudiogramPoint[] = (() => {
    if (
      word_results?.left?.srt_words?.length  ||
      word_results?.left?.wrs_words?.length  ||
      word_results?.right?.srt_words?.length ||
      word_results?.right?.wrs_words?.length
    ) {
      return buildAudigramData(
        word_results.left?.srt_words  ?? [],
        word_results.left?.wrs_words  ?? [],
        word_results.right?.srt_words ?? [],
        word_results.right?.wrs_words ?? [],
      );
    }
    return buildFallbackData(srt.left, srt.right, wrs.left, wrs.right);
  })();

  const leftSrt  = srtInterpretation(srt.left,  t);
  const rightSrt = srtInterpretation(srt.right, t);

  // Relative WRS — compares full audiogram curve to normative reference
  const leftRelWrs  = computeRelativeWRS(
    word_results?.left?.srt_words  ?? [],
    word_results?.left?.wrs_words  ?? [],
    wrs.left,
  );
  const rightRelWrs = computeRelativeWRS(
    word_results?.right?.srt_words ?? [],
    word_results?.right?.wrs_words ?? [],
    wrs.right,
  );
  const leftWrs  = wrsInterpretation(leftRelWrs,  t);
  const rightWrs = wrsInterpretation(rightRelWrs, t);

  return (
    <div ref={resultPanelRef} style={{ display: "flex", flex: "1 1 0", flexDirection: "column", minHeight: 0 }}>
    <div className="verbal-results">
      {/* ── Header ──────────────────────────────────────────────────────── */}
      <ResultHeader
        pillText={t("Audiometria słowna")}
        subtitle={subtitle}
        actionsRef={exportMenuRef}
        isExportMenuOpen={isExportMenuOpen}
        isExporting={isExporting}
        exportFormat={exportFormat}
        isExportDisabled={isExportDisabled}
        onToggleExportMenu={onToggleExportMenu}
        onExport={onExport}
        showAnalysisButton={showAnalysisButton}
        isAnalysisDisabled={isAnalysisDisabled}
        onAnalyze={onAnalyze}
        onEmailResult={onEmailResult}
        extraActions={
          onNewTest ? (
            <FormActionButton
              type="button"
              variant="secondary"
              size="sm"
              iconClassName="bi bi-arrow-repeat"
              onClick={onNewTest}
            >
              {t("Nowy test")}
            </FormActionButton>
          ) : undefined
        }
      />

      {children}

      {/* ── Summary table ───────────────────────────────────────────────── */}
      <div className="verbal-results__summary-grid">
        <div className="verbal-results__summary-card verbal-results__summary-card--left">
          <div className="verbal-results__ear-dot" style={{ backgroundColor: LEFT_COLOR }} />
          <strong>{t("Ucho lewe")}</strong>
          <div className="verbal-results__metric">
            <span className="verbal-results__metric-label">SRT</span>
            <span className="verbal-results__metric-value">
              {srt.left !== null ? `${srt.left} dB HL` : "—"}
            </span>
            <span className={`verbal-results__metric-interp ${leftSrt.className}`}>
              {leftSrt.label}
            </span>
          </div>
          <div className="verbal-results__metric">
            <span className="verbal-results__metric-label">WRS</span>
            <span className="verbal-results__metric-value">
              {leftRelWrs !== null ? `${leftRelWrs}%` : "—"}
              {leftRelWrs !== null && leftRelWrs !== wrs.left && (
                <span className="verbal-results__metric-ref-diff">
                  {` (${t("vs wzorzec")}: ${wrs.left}%)`}
                </span>
              )}
            </span>
            <span className={`verbal-results__metric-interp ${leftWrs.className}`}>
              {leftWrs.label}
            </span>
          </div>
        </div>

        <div className="verbal-results__summary-card verbal-results__summary-card--right">
          <div className="verbal-results__ear-dot" style={{ backgroundColor: RIGHT_COLOR }} />
          <strong>{t("Ucho prawe")}</strong>
          <div className="verbal-results__metric">
            <span className="verbal-results__metric-label">SRT</span>
            <span className="verbal-results__metric-value">
              {srt.right !== null ? `${srt.right} dB HL` : "—"}
            </span>
            <span className={`verbal-results__metric-interp ${rightSrt.className}`}>
              {rightSrt.label}
            </span>
          </div>
          <div className="verbal-results__metric">
            <span className="verbal-results__metric-label">WRS</span>
            <span className="verbal-results__metric-value">
              {rightRelWrs !== null ? `${rightRelWrs}%` : "—"}
              {rightRelWrs !== null && rightRelWrs !== wrs.right && (
                <span className="verbal-results__metric-ref-diff">
                  {` (${t("vs wzorzec")}: ${wrs.right}%)`}
                </span>
              )}
            </span>
            <span className={`verbal-results__metric-interp ${rightWrs.className}`}>
              {rightWrs.label}
            </span>
          </div>
        </div>
      </div>

      {/* ── Speech Audiogram ────────────────────────────────────────────── */}
      <div className="verbal-results__chart-wrap">
        <h4 className="verbal-results__chart-title">
          {t("Audiogram słowny")} — {t("Krzywa rozpoznawania mowy")}
        </h4>

        {/* Legend */}
        <div className="verbal-results__audiogram-legend">
          <span className="verbal-results__audiogram-legend-item">
            <svg width="18" height="18" viewBox="0 0 18 18"><text x="9" y="13" textAnchor="middle" fontSize="14" fontWeight="bold" fill={LEFT_COLOR}>×</text></svg>
            {t("Lewe — SRT")}
          </span>
          <span className="verbal-results__audiogram-legend-item">
            <svg width="18" height="18" viewBox="0 0 18 18"><rect x="3" y="3" width="12" height="12" fill={LEFT_COLOR} /></svg>
            {t("Lewe — WRS")}
          </span>
          <span className="verbal-results__audiogram-legend-item">
            <svg width="18" height="18" viewBox="0 0 18 18"><circle cx="9" cy="9" r="5" fill="none" stroke={RIGHT_COLOR} strokeWidth="2" /></svg>
            {t("Prawe — SRT")}
          </span>
          <span className="verbal-results__audiogram-legend-item">
            <svg width="18" height="18" viewBox="0 0 18 18"><polygon points="9,2 3,16 15,16" fill={RIGHT_COLOR} /></svg>
            {t("Prawe — WRS")}
          </span>
          <span className="verbal-results__audiogram-legend-item">
            <svg width="18" height="4" viewBox="0 0 18 4">
              <line x1="0" y1="2" x2="18" y2="2" stroke={REF_COLOR} strokeWidth="2" />
            </svg>
            {t("Wzorzec (norma)")}
          </span>
        </div>

        <ResponsiveContainer width="100%" height={340}>
          <ComposedChart
            data={audiogramData}
            margin={{ top: 14, right: 36, left: 4, bottom: 28 }}
          >
            <CartesianGrid strokeDasharray="3 3" stroke="rgba(180,195,215,0.7)" />
            <XAxis
              dataKey="dB"
              type="number"
              domain={[0, 100]}
              ticks={[0, 10, 20, 30, 40, 50, 60, 70, 80, 90, 100]}
              tick={{ fontSize: 11, fill: "#1a2030" }}
              label={{ value: "dB HL", position: "insideBottom", offset: -14, fontSize: 11, fill: "#6b7280" }}
            />
            <YAxis
              domain={[0, 100]}
              ticks={[0, 10, 20, 30, 40, 50, 60, 70, 80, 90, 100]}
              tickFormatter={v => `${v}%`}
              tick={{ fontSize: 11, fill: "#1a2030" }}
              width={40}
              label={{ value: "%", angle: -90, position: "insideLeft", offset: 14, fontSize: 11, fill: "#6b7280" }}
            />
            <Tooltip
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              formatter={(value: any, name: any) => {
                if (name === "ref") return [`${value}%`, t("Wzorzec (norma)")];
                return [
                  `${value}%`,
                  name === "left" ? t("Ucho lewe") : t("Ucho prawe"),
                ];
              }}
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              labelFormatter={(dB: any) => `${dB} dB HL`}
            />

            {/* 50% reference — SRT threshold */}
            <ReferenceLine
              y={50}
              stroke="#9ca3af"
              strokeDasharray="8 4"
              label={{ value: "50%  SRT", position: "insideRight", fontSize: 9, fill: "#6b7280" }}
            />
            {/* 84% reference — lower WRS normal */}
            <ReferenceLine
              y={84}
              stroke="#22c55e"
              strokeDasharray="6 3"
              label={{ value: "84%  WRS", position: "insideRight", fontSize: 9, fill: "#16a34a" }}
            />

            {/* Vertical SRT markers */}
            {srt.left !== null && (
              <ReferenceLine
                x={srt.left}
                stroke={LEFT_COLOR}
                strokeDasharray="4 3"
                strokeOpacity={0.6}
                label={{ value: `SRT L ${srt.left} dB`, position: "insideTopLeft", fontSize: 9, fill: LEFT_COLOR, angle: -90 }}
              />
            )}
            {srt.right !== null && (
              <ReferenceLine
                x={srt.right}
                stroke={RIGHT_COLOR}
                strokeDasharray="4 3"
                strokeOpacity={0.6}
                label={{ value: `SRT P ${srt.right} dB`, position: "insideTopRight", fontSize: 9, fill: RIGHT_COLOR, angle: -90 }}
              />
            )}

            {/* Normative reference curve */}
            <Line
              dataKey="ref"
              stroke={REF_COLOR}
              strokeWidth={2}
              strokeDasharray="6 3"
              type="monotone"
              dot={false}
              activeDot={false}
              isAnimationActive={false}
            />

            {/* Left ear curve */}
            <Line
              dataKey="left"
              stroke={LEFT_COLOR}
              strokeWidth={2}
              type="monotone"
              connectNulls
              dot={(props: { cx?: number; cy?: number; index?: number; payload?: AudiogramPoint }) => <LeftDot key={`ld-${props.index}`} cx={props.cx} cy={props.cy} payload={props.payload} />}
              activeDot={{ r: 6, fill: LEFT_COLOR }}
              isAnimationActive={false}
            />
            {/* Right ear curve */}
            <Line
              dataKey="right"
              stroke={RIGHT_COLOR}
              strokeWidth={2}
              type="monotone"
              connectNulls
              dot={(props: { cx?: number; cy?: number; index?: number; payload?: AudiogramPoint }) => <RightDot key={`rd-${props.index}`} cx={props.cx} cy={props.cy} payload={props.payload} />}
              activeDot={{ r: 6, fill: RIGHT_COLOR }}
              isAnimationActive={false}
            />
          </ComposedChart>
        </ResponsiveContainer>
      </div>

      {/* ── Interpretation scales ────────────────────────────────────────── */}
      <div className="verbal-results__chart-wrap verbal-results__chart-wrap--scales">
        <h4 className="verbal-results__chart-title">{t("Interpretacja wyników")}</h4>
        <ScaleBar
          zones={SRT_ZONES}
          max={110}
          valueLeft={srt.left}
          valueRight={srt.right}
          unit=" dB"
          title={`SRT — ${t("Próg rozumienia mowy (pogorszenie)")}`}
        />
        <ScaleBar
          zones={WRS_ZONES}
          max={100}
          valueLeft={leftRelWrs}
          valueRight={rightRelWrs}
          unit="%"
          title={`WRS — ${t("Wynik rozpoznawania słów (względem wzorca)")}`}
        />
      </div>

      {/* ── Disclaimer ──────────────────────────────────────────────────── */}
      <div className="test-panel__notice mt-3" role="note">
        <div className="test-panel__notice-title">
          <i className="bi bi-info-circle-fill" aria-hidden="true" />
          <span> {t("PAMIĘTAJ !")}</span>
        </div>
        <p className="test-panel__notice-text mb-0" style={{ color: "#374151" }}>
          {t("Badanie ma charakter wyłącznie informacyjny. Nie stanowi podstawy do diagnozy ani leczenia medycznego. W celu potwierdzenia wyników zalecamy umówienie się na profesjonalne badanie słuchu w naszym gabinecie, które jest wykonywane na certyfikowanym sprzęcie medycznym, co zapewnia wysoką precyzję i wiarygodność wyników.")}
        </p>
      </div>
    </div>
    </div>
  );
}
